import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card, ListGroup, Alert, Image } from 'react-bootstrap';
import axiosInstance from '../utils/axiosConfig';
import { FaCreditCard, FaPaypal, FaRupeeSign } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { currentUser } = useAuth() || { currentUser: null };
  
  // Debug: Log cart data
  useEffect(() => {
    console.log('Checkout page - Cart data:', cart);
  }, [cart]);
  
  const [formData, setFormData] = useState({
    firstName: currentUser?.firstName || currentUser?.name?.split(' ')[0] || '',
    lastName: currentUser?.lastName || currentUser?.name?.split(' ')[1] || '',
    email: currentUser?.email || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    paymentMethod: 'razorpay',
    cardName: '',
    cardNumber: '',
    expDate: '',
    cvv: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  // Calculate order totals
  const subtotal = cart?.items ? cart.items.reduce((sum, item) => sum + (typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)) * item.quantity, 0) : 0;
  // const shipping = subtotal > 0 ? 10 : 0;
  const shipping = 0;
  const tax = subtotal * 0.08; // 8% tax rate
  const total = subtotal + shipping + tax;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    const requiredFields = [
      'firstName', 'lastName', 'email', 'address', 
      'city', 'state', 'zipCode', 'country'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Payment validation
    if (formData.paymentMethod === 'credit') {
      if (!formData.cardName) newErrors.cardName = 'Name on card is required';
      if (!formData.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!/^\d{16}$/.test(formData.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Please enter a valid 16-digit card number';
      }
      
      if (!formData.expDate) {
        newErrors.expDate = 'Expiration date is required';
      } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.expDate)) {
        newErrors.expDate = 'Please use MM/YY format';
      }
      
      if (!formData.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (!/^\d{3,4}$/.test(formData.cvv)) {
        newErrors.cvv = 'CVV must be 3 or 4 digits';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to initialize Razorpay payment
  const initializeRazorpay = async (orderData) => {
    // Load Razorpay script dynamically
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Function to create a Razorpay payment
  const createRazorpayPayment = async (orderData) => {
    setLoading(true);
    
    // Check if cart is valid
    if (!cart?.items || cart.items.length === 0) {
      setErrors({ general: 'Cannot proceed with an empty cart' });
      setLoading(false);
      return;
    }
    
    try {
      const response = await initializeRazorpay();
      
      if (!response) {
        setErrors({
          general: 'Razorpay SDK failed to load. Please check your internet connection and try again.'
        });
        return;
      }

      // Get auth token if user is logged in
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Create Razorpay order via backend
      try {
        const orderResponse = await axiosInstance.post('/orders/create-razorpay-order', {
          totalAmount: total,
          currency: 'INR'
        });
        
        if (!orderResponse.data.orderId) {
          throw new Error('Failed to create Razorpay order: Missing order ID');
        }
        
        console.log('Razorpay order created:', orderResponse.data);
        
        const options = {
          key: orderResponse.data.key, // Key from backend
          amount: orderResponse.data.amount, // Amount in smallest currency unit
          currency: orderResponse.data.currency,
          name: "Telering Process Pvt. Ltd",
          description: "Purchase from Telering Process Pvt. Ltd",
          image: "https://example.com/your_logo.png",
          order_id: orderResponse.data.orderId,
          handler: async function (response) {
            try {
              console.log('Razorpay payment successful:', response);
              
              // Get auth token if user is logged in
              const token = localStorage.getItem('token');
              const headers = token ? { Authorization: `Bearer ${token}` } : {};
              
              // Verify payment with backend
              const verificationResponse = await axiosInstance.post('/orders/verify-razorpay-payment', {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                shippingAddress: {
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  address: formData.address,
                  city: formData.city,
                  state: formData.state,
                  zipCode: formData.zipCode,
                  country: formData.country
                },
                totalAmount: total,
                userId: currentUser?.id
              }, { headers });
              
              console.log('Payment verification response:', verificationResponse.data);
              
              if (verificationResponse.data.message === 'Order placed successfully') {
                clearCart();
                setOrderSuccess(true);
                
                // Extract order ID from response
                const confirmedOrderId = verificationResponse.data.order?.id || 'ORDER' + Math.random().toString(36).substr(2, 9);
                
                setTimeout(() => {
                  navigate(`/order-confirmation/${confirmedOrderId}`);
                }, 2000);
              } else {
                setErrors({
                  general: 'Payment was successful, but order processing failed. Please contact customer support.'
                });
              }
            } catch (error) {
              console.error('Payment verification error:', error);
              setErrors({ 
                general: 'There was a problem verifying your payment. Please contact customer support with this reference: ' + response.razorpay_payment_id
              });
            } finally {
              setLoading(false);
            }
          },
          prefill: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            contact: "" // Add phone number to your form if needed
          },
          notes: {
            address: formData.address
          },
          theme: {
            color: "#0d6efd"
          },
          modal: {
            ondismiss: function() {
              console.log('Payment modal dismissed');
              setLoading(false);
            }
          }
        };
        
        const razorpay = new window.Razorpay(options);
        
        // Handle Razorpay specific errors
        razorpay.on('payment.failed', function(response) {
          console.error('Razorpay payment failed:', response.error);
          setErrors({
            general: `Payment failed: ${response.error.description} (Code: ${response.error.code})`
          });
          setLoading(false);
        });
        
        razorpay.open();
      } catch (apiError) {
        console.error('Razorpay API error:', apiError);
        const errorMessage = apiError.response?.data?.details || apiError.response?.data?.message || apiError.message;
        setErrors({
          general: `Payment initialization failed: ${errorMessage}`
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Razorpay order creation error:', error);
      setLoading(false);
      setErrors({ 
        general: `There was a problem creating your payment: ${error.message}`
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }
    
    if (!cart?.items || cart.items.length === 0) {
      setErrors({ general: 'Your cart is empty' });
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare order data
      const orderData = {
        userId: currentUser?.id || null,
        items: cart.items,
        shippingAddress: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        paymentMethod: formData.paymentMethod,
        totalAmount: total
      };
      
      // Handle different payment methods
      if (formData.paymentMethod === 'razorpay') {
        await createRazorpayPayment(orderData);
        setLoading(false);
        return;
      }
      
      // For other payment methods (credit, paypal)
      // Example order creation - would typically go to your backend API
      /*
      const response = await axiosInstance.post('/orders', orderData);
      */
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock order ID for non-Razorpay payments
      const mockOrderId = 'ORDER' + Math.random().toString(36).substr(2, 9);
      
      // Clear cart after successful order
      clearCart();
      setOrderSuccess(true);
      
      // Redirect to order confirmation after a delay
      setTimeout(() => {
        navigate(`/order-confirmation/${mockOrderId}`);
      }, 2000);
      
    } catch (error) {
      console.error('Checkout error:', error);
      setErrors({ 
        general: 'There was a problem processing your order. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (orderSuccess) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="success" className="p-5">
          <h2 className="mb-4">Thank You for Your Order!</h2>
          <p className="mb-4">Your order has been received and is being processed.</p>
          <p>You will be redirected to the order confirmation page shortly...</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">Checkout</h1>
      
      {errors.general && (
        <Alert variant="danger">{errors.general}</Alert>
      )}
      
      {cart?.items.length === 0 ? (
        <Card className="text-center p-5">
          <Card.Body>
            <h3 className="mb-4">Your cart is empty</h3>
            <p className="mb-4">Add some products to your cart before proceeding to checkout.</p>
            <Link to="/products" className="btn btn-primary">
              Browse Products
            </Link>
          </Card.Body>
        </Card>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Row>
            {/* Checkout form */}
            <Col lg={8}>
              <Card className="mb-4">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Shipping Information</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="firstName">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="firstName" 
                          value={formData.firstName}
                          onChange={handleChange}
                          isInvalid={!!errors.firstName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.firstName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="lastName">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="lastName" 
                          value={formData.lastName}
                          onChange={handleChange}
                          isInvalid={!!errors.lastName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control 
                      type="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="address">
                    <Form.Label>Street Address</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="address" 
                      value={formData.address}
                      onChange={handleChange}
                      isInvalid={!!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row>
                    <Col md={5} className="mb-3">
                      <Form.Group controlId="city">
                        <Form.Label>City</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="city" 
                          value={formData.city}
                          onChange={handleChange}
                          isInvalid={!!errors.city}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.city}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3} className="mb-3">
                      <Form.Group controlId="state">
                        <Form.Label>State</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="state" 
                          value={formData.state}
                          onChange={handleChange}
                          isInvalid={!!errors.state}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.state}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group controlId="zipCode">
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="zipCode" 
                          value={formData.zipCode}
                          onChange={handleChange}
                          isInvalid={!!errors.zipCode}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.zipCode}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="country">
                    <Form.Label>Country</Form.Label>
                    <Form.Select 
                      name="country" 
                      value={formData.country}
                      onChange={handleChange}
                      isInvalid={!!errors.country}
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="India">India</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Payment Method</h4>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <div className="d-flex flex-column gap-2">
                      {/* <Form.Check
                        type="radio"
                        id="creditCard"
                        name="paymentMethod"
                        value="credit"
                        label={<span className="d-flex align-items-center"><FaCreditCard className="me-2" /> Credit Card</span>}
                        checked={formData.paymentMethod === 'credit'}
                        onChange={handleChange}
                        className="mb-2"
                      /> */}
                      {/* <Form.Check
                        type="radio"
                        id="paypal"
                        name="paymentMethod"
                        value="paypal"
                        label={<span className="d-flex align-items-center"><FaPaypal className="me-2" /> PayPal</span>}
                        checked={formData.paymentMethod === 'paypal'}
                        onChange={handleChange}
                        className="mb-2"
                      /> */}
                      <Form.Check
                        type="radio"
                        id="razorpay"
                        name="paymentMethod"
                        value="razorpay"
                        label={<span className="d-flex align-items-center"><FaRupeeSign className="me-2" /> Razorpay</span>}
                        checked={formData.paymentMethod === 'razorpay'}
                        onChange={handleChange}
                        className="mb-2"
                      />
                    </div>
                  </Form.Group>

                  {/* {formData.paymentMethod === 'credit' && (
                    <div className="border p-3 rounded mb-3">
                      <Form.Group className="mb-3" controlId="cardName">
                        <Form.Label>Name on Card</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="cardName" 
                          value={formData.cardName}
                          onChange={handleChange}
                          isInvalid={!!errors.cardName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cardName}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Form.Group className="mb-3" controlId="cardNumber">
                        <Form.Label>Card Number</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="cardNumber" 
                          value={formData.cardNumber}
                          onChange={handleChange}
                          isInvalid={!!errors.cardNumber}
                          placeholder="XXXX XXXX XXXX XXXX"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.cardNumber}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Row>
                        <Col md={6} className="mb-3">
                          <Form.Group controlId="expDate">
                            <Form.Label>Expiration Date</Form.Label>
                            <Form.Control 
                              type="text" 
                              name="expDate" 
                              value={formData.expDate}
                              onChange={handleChange}
                              isInvalid={!!errors.expDate}
                              placeholder="MM/YY"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.expDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Form.Group controlId="cvv">
                            <Form.Label>CVV</Form.Label>
                            <Form.Control 
                              type="text" 
                              name="cvv" 
                              value={formData.cvv}
                              onChange={handleChange}
                              isInvalid={!!errors.cvv}
                              placeholder="XXX"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.cvv}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  )} */}
                  
                  {formData.paymentMethod === 'paypal' && (
                    <Alert variant="info">
                      You will be redirected to PayPal to complete your purchase after reviewing your order.
                    </Alert>
                  )}

                  {formData.paymentMethod === 'razorpay' && (
                    <Alert variant="info">
                      <div className="d-flex align-items-center">
                        <Image 
                          src="https://razorpay.com/assets/razorpay-logo.svg"
                          alt="Razorpay"
                          style={{ height: '30px' }}
                          className="me-3"
                        />
                        <div>
                          <p className="mb-0">You will be redirected to Razorpay's secure payment gateway after placing your order.</p>
                        </div>
                      </div>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Order Summary */}
            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Order Summary</h4>
                </Card.Header>
                <Card.Body className="p-0">
                  <ListGroup variant="flush">
                    {cart?.items.map((item) => (
                      <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center py-3">
                        <div className="d-flex align-items-center">
                          <div style={{ width: '50px', height: '50px', marginRight: '10px' }}>
                            <img 
                              src={item.image || item.image_url || "https://via.placeholder.com/80"} 
                              alt={item.title || item.name}
                              className="img-fluid" 
                              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                            />
                          </div>
                          <div>
                            <p className="mb-0 fw-bold">{item.title || item.name}</p>
                            <small className="text-muted">Qty: {item.quantity}</small>
                          </div>
                        </div>
                        <span>₹{((typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)) * item.quantity).toFixed(2)}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  
                  <hr className="m-0" />
                  
                  <div className="p-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal</span>
                      <span>₹{(typeof subtotal === 'number' ? subtotal : parseFloat(subtotal || 0)).toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Shipping</span>
                      <span>₹{(typeof shipping === 'number' ? shipping : parseFloat(shipping || 0)).toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Tax</span>
                      <span>₹{(typeof tax === 'number' ? tax : parseFloat(tax || 0)).toFixed(2)}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between mb-0 fw-bold">
                      <span>Total</span>
                      <span>₹{(typeof total === 'number' ? total : parseFloat(total || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  size="lg" 
                  type="submit" 
                  disabled={loading || !cart?.items || cart.items.length === 0}
                >
                  {loading ? 'Processing...' : `Place Order ${formData.paymentMethod === 'razorpay' ? 'with Razorpay' : ''}`}
                </Button>
                <Link to="/cart" className="btn btn-outline-secondary">
                  Return to Cart
                </Link>
              </div>
              
              <Alert variant="info" className="mt-3">
                <small>
                  By placing your order, you agree to our <a href="#">Terms of Service</a> and 
                  <a href="#"> Privacy Policy</a>.
                </small>
              </Alert>
            </Col>
          </Row>
        </Form>
      )}
    </Container>
  );
};

export default CheckoutPage; 